import type { DataItemId } from '@pn/core/domain/data';
import { Frequency } from './frequency';
import type { ProductionItem } from './index';
import { toSIUnit } from '@pn/core/domain/units';
import { isNil, round } from 'lodash-es';

export function addProductionDerivatives(
  productionItems: ProductionItem[]
): ProductionItem[] {
  const enhancedProductionItems: ProductionItem[] = [];

  const grouped = productionItems.reduce<
    Record<DataItemId, Record<string, ProductionItem[]>>
  >((acc, item) => {
    const { id = '', date } = item;
    if (isNil(acc[id])) acc[id] = {};
    if (isNil(acc[id][date])) acc[id][date] = [];
    acc[id][date].push(item);
    return acc;
  }, {});

  Object.entries(grouped).forEach(([id, dates]) => {
    Object.entries(dates).forEach(([date, itemsOnDate]) => {
      /* Add existing items */
      enhancedProductionItems.push(...itemsOnDate);

      /* Add derivatives */
      enhancedProductionItems.push(
        generateDerivativeProductionItem({
          id,
          date,
          fluid: 'Water Cut',
          symbol: '%',
          itemsOnDate,
          valueFn: getWaterCutValue,
        }),
        generateDerivativeProductionItem({
          id,
          date,
          fluid: 'GOR',
          symbol: 'm3/m3',
          itemsOnDate,
          valueFn: getGasOilRatioValue,
        }),
        generateDerivativeProductionItem({
          id,
          date,
          fluid: 'WGR',
          symbol: 'm3/e3m3',
          itemsOnDate,
          valueFn: getWaterGasRatioValue,
        }),
        generateDerivativeProductionItem({
          id,
          date,
          fluid: 'CGR',
          symbol: 'm3/e3m3',
          itemsOnDate,
          valueFn: getCondensateGasRatioValue,
        })
      );
    });
  });

  /* Remove all derivatives that have no defined values */
  return ['Water Cut', 'GOR', 'WGR', 'CGR'].reduce(
    (acc, fluid) => removeEmptyFluids(acc, fluid),
    enhancedProductionItems
  );
}

function generateDerivativeProductionItem(params: {
  id: DataItemId | undefined;
  date: string;
  fluid: string;
  symbol: string;
  itemsOnDate: ProductionItem[];
  valueFn: (
    itemsOnDate: ProductionItem[],
    frequency: Frequency
  ) => number | undefined;
}): ProductionItem {
  const { id, date, fluid, symbol, itemsOnDate, valueFn } = params;

  return {
    date,
    flowType: 'other',
    fluid,
    producingDaily: toSIUnit({
      value: valueFn(itemsOnDate, Frequency.ProducingDaily),
      symbol,
    }),
    calendarDaily: toSIUnit({
      value: valueFn(itemsOnDate, Frequency.CalendarDaily),
      symbol,
    }),
    monthly: toSIUnit({
      value: valueFn(itemsOnDate, Frequency.Monthly),
      symbol,
    }),
    cumulative: toSIUnit({
      value: valueFn(itemsOnDate, Frequency.Cumulative),
      symbol,
    }),
    hours: undefined,
    count: itemsOnDate[0].count,
    id,
  };
}

function getWaterCutValue(
  itemsOnDate: ProductionItem[],
  frequency: Frequency
): number | undefined {
  const oilItem = itemsOnDate.find((item) => item.fluid === 'Oil');
  const waterItem = itemsOnDate.find((item) => item.fluid === 'Water');

  if (!isNil(oilItem) && !isNil(waterItem)) {
    const oilValue = oilItem[frequency].value;
    const waterValue = waterItem[frequency].value;

    if (isNonZeroNumber(oilValue) && isNonZeroNumber(waterValue)) {
      return round((waterValue / (oilValue + waterValue)) * 100, 1);
    }
  }

  return undefined;
}

function getGasOilRatioValue(
  itemsOnDate: ProductionItem[],
  frequency: Frequency
): number | undefined {
  const oilItem = itemsOnDate.find((item) => item.fluid === 'Oil');
  const gasItem = itemsOnDate.find((item) => item.fluid === 'Gas');

  if (!isNil(oilItem) && !isNil(gasItem)) {
    const oilValue = oilItem[frequency].value;
    const gasValue = gasItem[frequency].value;

    if (isNonZeroNumber(oilValue) && isNonZeroNumber(gasValue)) {
      return round((gasValue * 1000) / oilValue, 1);
    }
  }

  return undefined;
}

function getWaterGasRatioValue(
  itemsOnDate: ProductionItem[],
  frequency: Frequency
): number | undefined {
  const waterItem = itemsOnDate.find((item) => item.fluid === 'Water');
  const gasItem = itemsOnDate.find((item) => item.fluid === 'Gas');

  if (!isNil(waterItem) && !isNil(gasItem)) {
    const waterValue = waterItem[frequency].value;
    const gasValue = gasItem[frequency].value;

    if (isNonZeroNumber(waterValue) && isNonZeroNumber(gasValue)) {
      return round(waterValue / gasValue, 3);
    }
  }

  return undefined;
}

function getCondensateGasRatioValue(
  itemsOnDate: ProductionItem[],
  frequency: Frequency
): number | undefined {
  const condensateItem = itemsOnDate.find(
    (item) => item.fluid === 'Condensate'
  );
  const gasItem = itemsOnDate.find((item) => item.fluid === 'Gas');

  if (!isNil(condensateItem) && !isNil(gasItem)) {
    const condensateValue = condensateItem[frequency].value;
    const gasValue = gasItem[frequency].value;

    if (isNonZeroNumber(condensateValue) && isNonZeroNumber(gasValue)) {
      return round(condensateValue / gasValue, 3);
    }
  }

  return undefined;
}

function removeEmptyFluids(
  productionItems: ProductionItem[],
  fluid: string
): ProductionItem[] {
  const { matchingItems, nonMatchingItems } = productionItems.reduce<{
    matchingItems: ProductionItem[];
    nonMatchingItems: ProductionItem[];
  }>(
    (acc, item) => {
      if (item.fluid === fluid) {
        acc.matchingItems.push(item);
      } else {
        acc.nonMatchingItems.push(item);
      }
      return acc;
    },
    { matchingItems: [], nonMatchingItems: [] }
  );

  if (matchingItems.every((item) => isNil(item.monthly.value))) {
    return nonMatchingItems;
  } else {
    return productionItems;
  }
}

function isNonZeroNumber(value: number | undefined): value is number {
  return !isNil(value) && value !== 0;
}
